import { router } from '@/router'
import { browserPathToRouter, redirectToLogin } from '@/utils/authLoginRedirect'
import { refreshSessionOnce } from '@/utils/refreshSession'
import { type ApiFetcherArgs, tsRestFetchApi } from '@ts-rest/core'
import flagsmith from 'flagsmith'

let legacyRefreshPromise: Promise<Response> | null = null

/**
 * A drop-in replacement for the default ts-rest fetcher that transparently
 * handles expired access tokens:
 *
 * 1. Makes the original request (with credentials so the httpOnly access
 *    cookie is attached).
 * 2. If the response is a 401 on an authenticated route, triggers a single
 *    `POST /refresh` via `refreshSessionOnce`. Concurrent 401s (and the auth
 *    store) share that one in-flight request so we never spam the refresh
 *    endpoint or spend a rotating refresh token twice.
 * 3. On refresh success, retries the original request and returns the retry
 *    response.
 * 4. On refresh failure, hard-redirects to the login page.
 *
 * On unauthenticated routes (login, forgot-password, email-sent,
 * confirm-forgot-password) a 401 is the expected response when no session
 * exists — we let it bubble up to the caller without attempting a refresh
 * or redirect.
 *
 * Passed into `initClient` in `useClient.ts`, this gives every query/mutation
 * in `src/queries/*` seamless session renewal without any per-call-site
 * knowledge of auth.
 */

export const authenticatedFetch = async (args: ApiFetcherArgs) => {
  const response = await tsRestFetchApi({
    ...args,
    credentials: 'include',
  })

  // Prefer the resolved router state, but on a cold load the router has not
  // yet completed its initial navigation (START_LOCATION), so fall back to
  // resolving the URL after stripping the app base.
  const currentRoute = router.currentRoute.value
  const resolved = currentRoute.matched.length > 0 ? currentRoute : router.resolve(browserPathToRouter())
  const isUnauthenticatedRoute = resolved.meta.unauthenticated === true

  if (response.status === 401 && !isUnauthenticatedRoute) {
    if (flagsmith.hasFeature('feature/auth_session_bootstrap')) {
      const isSessionProbe = /\/(refresh|session)$/.test(args.path?.split('?')[0] ?? '')
      if (isSessionProbe) {
        return response
      }

      await refreshSessionOnce()
      return tsRestFetchApi({
        ...args,
        credentials: 'include',
      })
    }

    if (!legacyRefreshPromise) {
      legacyRefreshPromise = fetch(`${import.meta.env.VITE_API_BASE_URL}/refresh`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      }).finally(() => {
        legacyRefreshPromise = null
      })
    }

    const refreshResponse = await legacyRefreshPromise

    if (refreshResponse.status === 200) {
      return tsRestFetchApi({
        ...args,
        credentials: 'include',
      })
    }

    redirectToLogin()
    return response
  }

  return response
}
